import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  collection, query, orderBy, getDocs, doc, getDoc, 
  updateDoc, serverTimestamp, deleteDoc
} from 'firebase/firestore';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import ModalDialog from '../components/ModalDialog';
import { showError } from '../utils/notifications';
import { logEdit, logCreateUser, logDeleteUser } from '../services/auditService';
import { createNewUserWithoutSession } from '../utils/authUtils';

function UserManagement() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [activeSection, setActiveSection] = useState('user-management');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  
  // State for creating new user
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    nombre: '',
    cedula: '',
    telefono: '',
    rol: 'Usuario'
  });
  
  // State for editing user
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editUserData, setEditUserData] = useState({
    nombre: '',
    cedula: '',
    telefono: '',
    rol: '',
    status: 1
  });
  
  // State for modal dialogs
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false, 
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'confirm'
  });
  
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'alert'
  });
  // Available user roles
  const availableRoles = [
    { value: 'Admin', label: 'Administrador' },
    { value: 'Supervisor', label: 'Supervisor' },
    { value: 'Monitoreo', label: 'Monitoreo' },
    { value: 'Usuario', label: 'Usuario básico' },
    { value: 'CA', label: 'Controlador Autorizado' },
    { value: 'Auditor', label: 'Auditor' },
    { value: 'Recaudador', label: 'Recaudador' },
    { value: 'Fiscalizador', label: 'Fiscalizador' },
    { value: 'Financiero', label: 'Financiero' }
  ];

  // Fetch all users - Define fetchUsers with useCallback BEFORE using it
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'usuarios');
      const q = query(usersRef, orderBy('rol'), orderBy('nombre'));
      const querySnapshot = await getDocs(q);
      
      const usersList = [];
      querySnapshot.forEach((doc) => {
        usersList.push({
          id: doc.id,
          ...doc.data(),
          // Ensure fields exist
          nombre: doc.data().nombre || 'Sin nombre',
          rol: doc.data().rol || 'Usuario',
          status: doc.data().status === undefined ? 1 : doc.data().status,
          cedula: doc.data().cedula || '',
          telefono: doc.data().telefono || '',
          createdAt: doc.data().createdAt || null
        });
      });
      
      setUsers(usersList);
      console.log(`Usuarios cargados: ${usersList.length}`);
    } catch (error) {
      console.error("Error al cargar usuarios:", error);
      showError("Error", "No se pudieron cargar los usuarios");
    } finally {
      setLoading(false);
    }
  }, []);

  // Check user access and fetch users
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // Verify if the user profile exists in Firestore
          const userDocRef = doc(db, 'usuarios', currentUser.email);
          const userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            showError("Usuario no encontrado", "Tu perfil no existe en el sistema");
            await auth.signOut();
            navigate('/login');
            return;
          }
          
          // Store user data
          setUser(currentUser);
          
          // Store user role
          const userData = userDoc.data();
          const role = (userData.rol || 'Usuario').trim();
          setUserRole(role);
          
          // Check if user is Admin
          if (role !== 'Admin') {
            showError("Acceso denegado", "Solo administradores pueden gestionar usuarios");
            navigate('/dashboard');
            return;
          }
          
          // Fetch users
          fetchUsers();
        } catch (error) {
          console.error("Error al verificar perfil de usuario:", error);
          showError("Error", "Ocurrió un problema al verificar tu perfil");
          await auth.signOut();
          navigate('/login');
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, fetchUsers]);

  // Inactivar todos los CAs de una sola vez (solo activos, excluye ocultos)
  const handleInactivarTodosCA = () => {
    const caActivos = users.filter(u => u.rol === 'CA' && u.status === 1 && !u.oculto);
    if (caActivos.length === 0) {
      setAlertModal({ isOpen: true, title: 'Sin cambios', message: 'No hay Controladores Autorizados activos para inactivar.', type: 'alert' });
      return;
    }
    setConfirmModal({
      isOpen: true,
      title: 'Inactivar todos los CAs',
      message: `¿Estás seguro? Se inactivarán ${caActivos.length} Controladores Autorizados activos.`,
      type: 'confirm',
      onConfirm: async () => {
        setConfirmModal(m => ({ ...m, isOpen: false }));
        try {
          const activoIds = new Set(caActivos.map(u => u.id));
          await Promise.all(caActivos.map(u =>
            updateDoc(doc(db, 'usuarios', u.id), { status: 0, lastModified: serverTimestamp() })
          ));
          setUsers(prev => prev.map(u => activoIds.has(u.id) ? { ...u, status: 0 } : u));
          setAlertModal({ isOpen: true, title: 'Completado', message: `${caActivos.length} CAs inactivados correctamente.`, type: 'success' });
        } catch (err) {
          setAlertModal({ isOpen: true, title: 'Error', message: 'No se pudieron inactivar los CAs: ' + err.message, type: 'error' });
        }
      }
    });
  };

  // Apply filters to users list
  const filteredUsers = users.filter((user) => {
    const matchesSearch = searchTerm === '' || 
      user.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.id.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesRole = roleFilter === '' || user.rol === roleFilter;
    
    return matchesSearch && matchesRole;
  });
  // Handle creating a new user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!newUser.email || !newUser.nombre || !newUser.cedula || !newUser.rol) {
      showError("Campos incompletos", "Todos los campos con * son obligatorios");
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      showError("Email inválido", "Por favor ingresa un email válido");
      return;
    }
    
    // Check if email already exists in the current users list
    const existingUser = users.find(user => user.id.toLowerCase() === newUser.email.toLowerCase());
    if (existingUser) {
      setAlertModal({
        isOpen: true,
        title: "Usuario ya existe",
        message: `Ya existe un usuario con el email "${newUser.email}".\n\nNombre: ${existingUser.nombre}\nRol: ${existingUser.rol}\nEstado: ${existingUser.status === 1 ? 'Activo' : 'Inactivo'}`,
        type: "warning"
      });
      return;
    }
    
    // Check for similar emails to help identify potential duplicates
    const similarEmails = users.filter(user => {
      const userEmail = user.id.toLowerCase();
      const newEmail = newUser.email.toLowerCase();
      const emailParts = newEmail.split('@');
      const userEmailParts = userEmail.split('@');
      
      // Check if same domain and similar username
      return emailParts[1] === userEmailParts[1] && 
             (userEmailParts[0].includes(emailParts[0]) || emailParts[0].includes(userEmailParts[0]));
    });
    
    if (similarEmails.length > 0) {
      const similarEmailsList = similarEmails.map(u => `• ${u.id} (${u.nombre})`).join('\n');
      const shouldContinue = window.confirm(
        `⚠️ Se encontraron emails similares:\n\n${similarEmailsList}\n\n¿Está seguro de que desea crear un nuevo usuario con "${newUser.email}"?`
      );
      if (!shouldContinue) {
        setLoading(false);
        return;
      }
    }
      try {
      setLoading(true);
      
      // Check if current user is authenticated
      if (!auth.currentUser) {
        throw new Error("Usuario no autenticado. Por favor, inicie sesión nuevamente.");
      }
        // Crear el usuario con nuestra función de utilidad
      const result = await createNewUserWithoutSession(
        {
          ...newUser,
          status: 1
        },
        newUser.cedula,
        auth.currentUser?.email || 'unknown@user.com',
        true  // Prevent logout when creating supervisors/users
      );
      
      if (!result.success) {
        throw new Error(result.error);
      }
        // Log the user creation (with null check)
      if (auth.currentUser) {
        await logCreateUser(
          {email: auth.currentUser.email, rol: userRole},
          newUser
        );
      }
      
      // Show success message
      setAlertModal({
        isOpen: true,
        title: "Usuario Creado",
        message: `El usuario ${newUser.nombre} ha sido creado exitosamente con el rol de ${newUser.rol}.`,
        type: "success"
      });
      
      // Reset form and close
      setNewUser({
        email: '',
        nombre: '',
        cedula: '',
        telefono: '',
        rol: 'Usuario'
      });
      setIsCreatingUser(false);
      
      // Refresh users list
      fetchUsers();    } catch (error) {
      console.error("Error al crear usuario:", error);
      
      // Proporcionar mensajes de error más específicos
      let errorMessage = "No se pudo crear el usuario";
      let errorTitle = "Error";
      
      if (error.message.includes("email ya está registrado") || 
          error.message.includes("email-already-in-use")) {
        errorTitle = "Email ya registrado";
        errorMessage = "Este email ya está en uso. Por favor:\n\n" +
                      "• Use un email diferente\n" +
                      "• Verifique si el usuario ya existe en la lista\n" +
                      "• Contacte al administrador si necesita recuperar una cuenta existente";
      } else if (error.message.includes("Ya existe un usuario")) {
        errorTitle = "Usuario duplicado";
        errorMessage = "Ya existe un usuario con este email en el sistema. Verifique la lista de usuarios.";
      } else {
        errorMessage = `${errorMessage}: ${error.message}`;
      }
      
      setAlertModal({
        isOpen: true,
        title: errorTitle,
        message: errorMessage,
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle editing user
  const startEditingUser = (userData) => {
    setEditingUserId(userData.id);
    setEditUserData({
      nombre: userData.nombre || '',
      cedula: userData.cedula || '',
      telefono: userData.telefono || '',
      rol: userData.rol || 'Usuario',
      status: userData.status === undefined ? 1 : userData.status
    });
    setIsEditingUser(true);
  };

  // Save edited user
  const handleSaveUser = async (e) => {
    e.preventDefault();
    
    if (!editingUserId) return;
    
    // Validate required fields
    if (!editUserData.nombre || !editUserData.rol) {
      showError("Campos incompletos", "El nombre y rol son obligatorios");
      return;
    }
    
    try {
      setLoading(true);
      
      // Get current user data for audit log
      const userDocRef = doc(db, 'usuarios', editingUserId);
      const userDoc = await getDoc(userDocRef);
      const currentData = userDoc.exists() ? userDoc.data() : {};
      
      // Update user in Firestore
      const updatedData = {
        ...editUserData,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || 'unknown@user.com'
      };
      
      await updateDoc(userDocRef, updatedData);
      
      // Log the edit (with null check)
      if (auth.currentUser) {
        await logEdit(
          {email: auth.currentUser.email, rol: userRole},
          'usuarios',
          editingUserId,
          currentData,
          updatedData
        );
      }
      
      // Show success message
      setAlertModal({
        isOpen: true,
        title: "Usuario Actualizado",
        message: `El usuario ${editUserData.nombre} ha sido actualizado exitosamente.`,
        type: "success"
      });
      
      // Reset and close edit form
      setEditingUserId(null);
      setIsEditingUser(false);
      
      // Refresh users list
      fetchUsers();
    } catch (error) {
      console.error("Error al actualizar usuario:", error);
      showError("Error", `No se pudo actualizar el usuario: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete user
  const confirmDeleteUser = (userData) => {
    setConfirmModal({
      isOpen: true,
      title: "Confirmar Eliminación",
      message: `¿Está seguro que desea eliminar al usuario ${userData.nombre}? Esta acción no se puede deshacer.`,
      type: "confirm",
      onConfirm: async () => {
        try {
          setLoading(true);
          setConfirmModal(prev => ({...prev, isOpen: false}));
            // Delete the user document
          await deleteDoc(doc(db, 'usuarios', userData.id));
          
          // Log the deletion (with null check)
          if (auth.currentUser) {
            await logDeleteUser(
              {email: auth.currentUser.email, rol: userRole},
              userData
            );
          }
          
          // Show success message
          setAlertModal({
            isOpen: true,
            title: "Usuario Eliminado",
            message: `El usuario ${userData.nombre} ha sido eliminado exitosamente.`,
            type: "success"
          });
          
          // Refresh users list
          fetchUsers();
        } catch (error) {
          console.error("Error al eliminar usuario:", error);
          showError("Error", `No se pudo eliminar el usuario: ${error.message}`);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Toggle user status
  const toggleUserStatus = async (userData) => {
    const newStatus = userData.status === 1 ? 0 : 1;
    const statusText = newStatus === 1 ? "activar" : "desactivar";
    
    setConfirmModal({
      isOpen: true,
      title: `Confirmar ${statusText}`,
      message: `¿Está seguro que desea ${statusText} al usuario ${userData.nombre}?`,
      type: "confirm",
      onConfirm: async () => {
        try {
          setLoading(true);
          setConfirmModal(prev => ({...prev, isOpen: false}));
          
          // Update user status
          await updateDoc(doc(db, 'usuarios', userData.id), {
            status: newStatus,
            updatedAt: serverTimestamp()
          });
          
          // Show success message
          setAlertModal({
            isOpen: true,
            title: "Estado Actualizado",
            message: `El usuario ${userData.nombre} ha sido ${newStatus === 1 ? 'activado' : 'desactivado'} exitosamente.`,
            type: "success"
          });
          
          // Refresh users list
          fetchUsers();
        } catch (error) {
          console.error("Error al cambiar estado:", error);
          showError("Error", `No se pudo cambiar el estado del usuario: ${error.message}`);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      let date;
      if (typeof dateString === 'string') {
        date = new Date(dateString);
      } else if (dateString.seconds) {
        date = new Date(dateString.seconds * 1000);
      } else {
        return 'Fecha inválida';
      }
      
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Mobile Header */}
      <MobileHeader 
        title="Gestión de Usuarios" 
        userEmail={user?.email}
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      
      {/* Add top spacing for mobile to prevent content from being hidden under header */}
      <div className="md:hidden h-14"></div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Overlay for mobile menu */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}

        {/* Sidebar */}
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {/* Top Header (desktop only) */}
          <header className="bg-white shadow px-6 py-4 hidden md:block">
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-bold text-gray-900">Gestión de Usuarios</h1>
              <div className="text-right flex items-center">
                <div className="mr-4">
                  <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                  <p className="text-xs text-gray-500">{userRole || "Usuario"}</p>
                </div>
                <button 
                  onClick={async () => {
                    try {
                      localStorage.removeItem('userRole');
                      await auth.signOut();
                      navigate('/login');
                    } catch (error) {
                      console.error("Error al cerrar sesión:", error);
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1 rounded"
                >
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="p-6">
            {/* Filters & Actions */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 items-center justify-between">
                <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 w-full md:w-auto">
                  <div className="w-full md:w-64">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Buscar por nombre o email..."
                      className="border border-gray-300 rounded px-3 py-2 w-full"
                    />
                  </div>
                  <div className="w-full md:w-auto">
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="border border-gray-300 rounded px-3 py-2 w-full"
                    >
                      <option value="">Todos los roles</option>
                      {availableRoles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                  <button
                    onClick={handleInactivarTodosCA}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded text-sm w-full md:w-auto"
                  >
                    Inactivar todos los CAs
                  </button>
                  <button
                    onClick={() => setIsCreatingUser(true)}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm w-full md:w-auto"
                  >
                    Crear Nuevo Usuario
                  </button>
                </div>
              </div>
            </div>

            {/* Users List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">Usuarios del Sistema ({filteredUsers.length})</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cédula
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Teléfono
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rol
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Creado
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.map((userData) => (
                      <tr key={userData.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{userData.nombre}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{userData.id}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{userData.cedula || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{userData.telefono || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${userData.rol === 'Admin' ? 'bg-purple-100 text-purple-800' : 
                              userData.rol === 'Supervisor' ? 'bg-blue-100 text-blue-800' :
                                userData.rol === 'Monitoreo' ? 'bg-cyan-100 text-cyan-800' :
                                userData.rol === 'CA' ? 'bg-green-100 text-green-800' :
                                  userData.rol === 'Auditor' ? 'bg-yellow-100 text-yellow-800' :
                                    userData.rol === 'Recaudador' ? 'bg-indigo-100 text-indigo-800' :
                                      userData.rol === 'Fiscalizador' ? 'bg-orange-100 text-orange-800' :
                                        userData.rol === 'Financiero' ? 'bg-teal-100 text-teal-800' :
                                        'bg-gray-100 text-gray-800'
                            }`}>
                            {userData.rol}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${userData.status === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {userData.status === 1 ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(userData.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => startEditingUser(userData)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => toggleUserStatus(userData)}
                              className={`${
                                userData.status === 1 
                                  ? 'text-yellow-600 hover:text-yellow-900' 
                                  : 'text-green-600 hover:text-green-900'
                              }`}
                              title={userData.status === 1 ? 'Desactivar' : 'Activar'}
                            >
                              {userData.status === 1 ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => confirmDeleteUser(userData)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">
                          No se encontraron usuarios
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Create User Modal */}
      {isCreatingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Crear Nuevo Usuario</h3>
                <button
                  onClick={() => setIsCreatingUser(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleCreateUser}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email *</label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre completo *</label>
                    <input
                      type="text"
                      value={newUser.nombre}
                      onChange={(e) => setNewUser({...newUser, nombre: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cédula *</label>
                    <input
                      type="text"
                      value={newUser.cedula}
                      onChange={(e) => setNewUser({...newUser, cedula: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">La cédula será la contraseña inicial</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                    <input
                      type="text"
                      value={newUser.telefono}
                      onChange={(e) => setNewUser({...newUser, telefono: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rol *</label>
                    <select
                      value={newUser.rol}
                      onChange={(e) => setNewUser({...newUser, rol: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                      required
                    >
                      {availableRoles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsCreatingUser(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                  >
                    {loading ? 'Creando...' : 'Crear Usuario'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {isEditingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Editar Usuario</h3>
                <button
                  onClick={() => setIsEditingUser(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleSaveUser}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={editingUserId || ''}
                      className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm py-2 px-3 bg-gray-100"
                      disabled
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre completo *</label>
                    <input
                      type="text"
                      value={editUserData.nombre}
                      onChange={(e) => setEditUserData({...editUserData, nombre: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cédula</label>
                    <input
                      type="text"
                      value={editUserData.cedula}
                      onChange={(e) => setEditUserData({...editUserData, cedula: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                    <input
                      type="text"
                      value={editUserData.telefono}
                      onChange={(e) => setEditUserData({...editUserData, telefono: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rol *</label>
                    <select
                      value={editUserData.rol}
                      onChange={(e) => setEditUserData({...editUserData, rol: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                      required
                    >
                      {availableRoles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Estado</label>
                    <div className="mt-2">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          checked={editUserData.status === 1}
                          onChange={() => setEditUserData({...editUserData, status: 1})}
                          className="form-radio h-4 w-4 text-blue-600"
                        />
                        <span className="ml-2 text-gray-700">Activo</span>
                      </label>
                      <label className="inline-flex items-center ml-6">
                        <input
                          type="radio"
                          checked={editUserData.status === 0}
                          onChange={() => setEditUserData({...editUserData, status: 0})}
                          className="form-radio h-4 w-4 text-blue-600"
                        />
                        <span className="ml-2 text-gray-700">Inactivo</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsEditingUser(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                  >
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ModalDialog
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({...prev, isOpen: false}))}
        type={confirmModal.type}
      />
      
      {/* Alert Modal */}
      <ModalDialog
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        onConfirm={() => setAlertModal(prev => ({...prev, isOpen: false}))}
        type={alertModal.type}
      />
    </div>
  );
}

export default UserManagement;
